import { useCallback, useState } from "react";
import EndPoints from "../config/EndPoint";
import useFetch, { ServiceParams, ServiceResponse } from "../hooks/useFetch";
import {
  ServiceEmailSolicitudPayload,
  ServiceEmailSolicitudServicesPayload,
} from "../models/ServiceEmailSolicitudModel";

type Events = Partial<{
  onSuccess: (r: ServiceResponse) => void;
  onError: (r: ServiceResponse) => void;
}>;

const useServiceEmailSolicitudesService = () => {
  const { doGet, doPost, doPut, doDelete } = useFetch();
  const [fetching, setFetching] = useState<Record<string, boolean>>({});

  const setFlag = useCallback((key: string, value: boolean) => {
    setFetching((prev) => ({ ...prev, [key]: value }));
  }, []);

  const listAll = useCallback(
    (filters?: Record<string, any>, events: Events = {}, signal?: AbortSignal) => {
      const qs = filters
        ? Object.entries(filters)
            .filter(([, value]) => value !== undefined && value !== null && value !== "")
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
            .join("&")
        : "";
      return doGet({
        url: `${EndPoints.SERVICES.EMAILS_SOLICITUDES_ALL}${qs ? `?${qs}` : ""}`,
        setFetching: (value) => setFlag("listAll", value),
        signal,
        ...events,
      } as ServiceParams);
    },
    [doGet, setFlag]
  );

  const detail = useCallback(
    (emailId: number, events: Events = {}, signal?: AbortSignal) =>
      doGet({
        url: EndPoints.SERVICES.EMAILS_DETAIL.replace(":id", String(emailId)),
        setFetching: (value) => setFlag("detail", value),
        signal,
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  const list = useCallback(
    (emailId: number, events: Events = {}, signal?: AbortSignal) =>
      doGet({
        url: EndPoints.SERVICES.EMAILS_SOLICITUDES_LIST.replace(":emailId", String(emailId)),
        setFetching: (value) => setFlag("list", value),
        signal,
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  const store = useCallback(
    (emailId: number, payload: ServiceEmailSolicitudPayload, events: Events = {}) =>
      doPost({
        url: EndPoints.SERVICES.EMAILS_SOLICITUDES_STORE.replace(":emailId", String(emailId)),
        body: payload,
        setFetching: (value) => setFlag("store", value),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  const update = useCallback(
    (id: number, payload: Partial<ServiceEmailSolicitudPayload>, events: Events = {}) =>
      doPut({
        url: EndPoints.SERVICES.EMAILS_SOLICITUDES_UPDATE.replace(":id", String(id)),
        body: payload,
        setFetching: (value) => setFlag("update", value),
        ...events,
      } as ServiceParams),
    [doPut, setFlag]
  );

  const remove = useCallback(
    (id: number, events: Events = {}) =>
      doDelete({
        url: EndPoints.SERVICES.EMAILS_SOLICITUDES_DELETE.replace(":id", String(id)),
        setFetching: (value) => setFlag("remove", value),
        ...events,
      } as ServiceParams),
    [doDelete, setFlag]
  );

  const storeServices = useCallback(
    (solicitudId: number, payload: ServiceEmailSolicitudServicesPayload, events: Events = {}) =>
      doPost({
        url: EndPoints.SERVICES.EMAILS_SOLICITUD_SERVICES_STORE.replace(":solicitudId", String(solicitudId)),
        body: payload,
        setFetching: (value) => setFlag("storeServices", value),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  const removeServiceLink = useCallback(
    (id: number, events: Events = {}) =>
      doDelete({
        url: EndPoints.SERVICES.EMAILS_SOLICITUD_SERVICE_DELETE.replace(":id", String(id)),
        setFetching: (value) => setFlag("removeServiceLink", value),
        ...events,
      } as ServiceParams),
    [doDelete, setFlag]
  );

  return {
    fetching,
    listAll,
    detail,
    list,
    store,
    update,
    remove,
    storeServices,
    removeServiceLink,
  };
};

export default useServiceEmailSolicitudesService;
